/**
 * @file quotes/index.ts
 * @description Quotes service to manage all the quotes related operations.
 *
 * @author smendonca
 *
 */

/**
 * Imports.
 */
// Modules
import { isEmpty, isNil } from 'lodash';
// Help
import * as _func from '../../functions/quotes';
import * as _cache from '../cache';
import { getCurrentContract } from '../contract';
import * as _datetime from '../datetime';
import * as _payPlan from '../plan';
import * as _vehicleServices from '../Vehicle';
import { getProduct } from '../product';
import { Semaphore } from '../semaphore';
// Models
import * as ProductModels from '../../functions/product/product.model';
import * as QuotesModels from '../../functions/quotes/quotes.model';
import * as RatesModels from '../../functions/rates/rates.model';
import * as PaymentPlanModels from '../plan/model';
import * as QuotesServiceModels from './model';
// import { Plan } from '../../functions/paymentPlan/paymentPlan.model';
// Content
import resources from '../../content/coverages.json';

// semaphore instance to prevent components to make multiple api requests for a quote.
// subsequent calls are read from the cache
const quoteSemaphore = new Semaphore();

/**
 * Builds get quotes request object.
 * @returns Get quotes request object.
 */
export const buildGetQuotesRequest = (
  useEstimatedMiles = false
): QuotesModels.QuotesRequest => {
  const car = _vehicleServices.getVehicle();
  const miles = useEstimatedMiles
    ? Number(car?.estCurrentMiles ?? '0')
    : car.miles;
  const carMiles = (car?.miles || 0) > 0 ? car.miles : Number(car?.estCurrentMiles ?? '0');

  return {
    vin: car.vin,
    vehicleOdometer: miles || carMiles || 0,
    vehicleYear: car?.year?.toString() ?? '',
    vehicleMake: car?.make?.toString() ?? '',
    vehicleModel: car?.model?.toString() ?? '',
    saleDate: _datetime.now('YYYY-MM-DD') as string,
    zipCode: car.zip ?? '',
    commercialUse: car.commercialUse,
    snowPlowUse: car.snowPlowUse,
    estYearMiles: car.estYearlyMiles,
    state: car.state ?? '',
    vehicleCategory: car?.category ?? '',
    surcharges: car.surcharges
  };
};

/**
 * Gets the plan based on passed parameters.
 * @param programs Program list to match get the plan.
 * @param coverageCode Coverage code to filter.
 * @param term Term to filter.
 * @returns Matching plan.
 */
const getPlan = (
  programs: QuotesModels.Program,
  rate: RatesModels.Selected
): QuotesModels.Plan | undefined => {
  const coverage = programs.Coverages.find(
    (coverage: QuotesModels.Coverage) =>
      coverage['@Code'] === rate.category.covId // Coverage code.
  );

  return coverage?.Plans?.find(
    (plan: QuotesModels.Plan) =>
      Number(plan['@TMonths']) === rate.term.termMonths &&
      Number(plan['@TMiles']) === rate.term.miles &&
      Number(plan?.Deductible?.['@Amount']) === rate.term.deductible
  );
};

/**
 * Gets cached rate.
 * @returns Cached rate.
 */
export const getCachedRate = (): RatesModels.Selected =>
  _cache.getItem<RatesModels.Selected>(_cache.Key.rate);

/**
 * Gets cached program.
 * @returns Cached program.
 */
const getCachedProgram = (): RatesModels.Program => {
  const document = _cache.getItem<RatesModels.Document>(_cache.Key.rates);
  return document?.program;
};

/**
 * Updates cached product.
 * @param plan Plan information.
 */
const updateProductCache = (
  plan: QuotesModels.Plan,
  program: QuotesModels.Program
): void => {
  let product = _cache.getItem<ProductModels.default>(_cache.Key.product);
  product = {
    ...product,
    planCode: plan['@Name'],
    dealerCost: Number(plan?.Deductible?.DealerCost ?? 0),
    retailCost: Number(plan?.Deductible?.RetailCost ?? 0),
    termMiles: plan['@TMiles'],
    termMonths: plan['@TMonths'],
    deductibleCode: plan.Deductible['@Sur'],
    deductibleAmount: Number(plan?.Deductible?.['@Amount'] ?? '0'),
    saleDate: _datetime.now() as Date,
    taxRate: Number(plan.Deductible.SalesTaxPercent),
    category: program.Type,
    programPrefix: program.Prefix,
    contractCode: program.Value,
    taxAmount: Number(plan.Deductible.SalesTaxAmount),
    upgradePrograms: program.UpgradePrograms,
    basePrograms: program.BasePrograms
  };

  _cache.setItem<ProductModels.default>(_cache.Key.product, product);
};

/**
 * Updates cached payment plan.
 * @param plan Plan information.
 */
const updatePaymentPlanCache = (plan: QuotesModels.Plan): void => {
  let paymentPlan = _cache.getItem<PaymentPlanModels.IPlan>(_cache.Key.plan);

  const planAmount =
    plan?.Deductible?.RetailCost || plan?.Deductible?.DealerCost || 0;

  paymentPlan = {
    ...paymentPlan,
    amount: Number(planAmount)
  };

  _cache.setItem<PaymentPlanModels.IPlan>(_cache.Key.plan, paymentPlan);
};

/**
 * Verifies if plan and term matches.
 * @param plan Plan fetched from quotes
 * @param term Selected term.
 * @returns true if term matches plan, otherwise, false.
 */
const termMatchesPlan = async (
  plan: QuotesModels.Plan,
  term: RatesModels.Term
): Promise<boolean> => {
  const planPrice = Number(
    plan?.Deductible.RetailCost || plan?.Deductible.DealerCost
  );
  return (
    planPrice === term.regularPrice &&
    Number(plan?.['@TMonths'] ?? 0) === term.termMonths &&
    Number(plan?.['@TMiles'] ?? 0) === term.miles &&
    Number(plan?.Deductible?.['@Amount']) === term.deductible
  );
};

/**
 * gets the cached quote validating for key data point changes
 * @returns a validated quote response
 */
export const getQuoteAndValidate = async (
  useEstimatedMiles = false
): Promise<QuotesModels.GetQuotesResponse> => {
  const request = buildGetQuotesRequest(useEstimatedMiles);
  const cache = _cache.getItem<QuotesServiceModels.CachedQuotes>(
    _cache.Key.quotesCache
  );

  let resp = { statusCode: -1 } as QuotesModels.GetQuotesResponse; // default to invalid quote

  if (cache && cache.response) {
    resp = cache.response;
    // get a hash and compare the surcharge collection
    const reqSurcharges = request.surcharges ? request.surcharges.map((i) => i.Code).join() : '';
    const cacheSurcharges = cache.request.surcharges ? cache.request.surcharges.map((i) => i.Code).join() : '';

    const reuseCache =
      request.vin === cache.request.vin &&
      request.vehicleOdometer === cache.request.vehicleOdometer &&
      request.zipCode === cache.request.zipCode &&
      request.commercialUse === cache.request.commercialUse &&
      request.snowPlowUse === cache.request.snowPlowUse &&
      request.estYearMiles === cache.request.estYearMiles &&
      request.state === cache.request.state &&
      reqSurcharges === cacheSurcharges;

    if (!reuseCache) {
      // reset the plan to check for changes
      resp.status = -1;
    }
  }

  return resp;
};

/**
 * Gets the product configuration for the site.
 * @param data Dto request object.
 * @param type The rates type to fetch ppm/vsc.
 * @param programId Program identifier.
 * @returns Quotes for the given parameters.
 */
export const getQuotes = async (
  useEstimatedMiles = false
): Promise<QuotesModels.GetQuotesResponse> => {
  const resp = await getQuoteAndValidate(useEstimatedMiles);
  if (resp && resp.statusCode !== -1 && resp.status !== -1) {
    quoteSemaphore.active = false;
    return resp;
  }
  if (quoteSemaphore.active && resp.statusCode !== -1 && resp.status !== -1) {
    return await getQuotes();
  }
  quoteSemaphore.active = true;
  const request = buildGetQuotesRequest(useEstimatedMiles);
  const rate = getCachedRate();
  const program = getCachedProgram();
  const quotes = await _func.getQuotes(
    request,
    program?.category,
    rate?.product?.programId
  );
  if (quotes?.statusCode === 500 || quotes.status === 'false') {
    _cache.removeItem(_cache.Key.quotesCache);
    throw new Error('Process failed while trying to get quotes.');
  }

  _cache.setItem<QuotesServiceModels.CachedQuotes>(_cache.Key.quotesCache, {
    request,
    response: quotes
  });

  // Updates vehicle cache.
  _vehicleServices.updateVehicleCache(quotes?.vin);
  _vehicleServices.mergeVehicleToCustomer();

  return quotes;
};

/**
 * Returns the preferred program or the program matching the programId parameter
 * @param useEstimatedMiles use the mileage information
 * @param programId programid to select from the collection
 */
export const getSingleQuote = async (
  useEstimatedMiles = false,
  programId?: string
): Promise<QuotesModels.Program> => {
  const quotes = await getQuotes(useEstimatedMiles);
  // use the default program id if none was provided
  const id = programId ?? quotes?.programId;
  // default to the first quote
  let program = quotes?.programs[0];
  if (quotes && quotes.programs && quotes.programs.length > 0) {
    if (id) {
      // find the target program. Nothing may be found
      const targetProgram = quotes.programs.find((i) => i.Value === id);
      if (targetProgram) {
        program = targetProgram;
      }
    }
  }

  return program;
};

/**
 * Set quotes for the products.
 * @param program Program identifier
 * @param vin Vin of the car.
 * @param zip Zip code.
 * @param miles Miles of the car.
 */
export const setQuotes = async (): Promise<void> => {
  const rate = getCachedRate();

  // TODO: make sure to pass the select programId to getSingleQuote
  const product = getProduct();
  const quotes = await getSingleQuote(false, product?.contractCode);
  const plan = getPlan(quotes, rate);

  // Throws error if no quote is found for coverage and term identifiers.
  if (isNil(quotes) || isEmpty(quotes) || isNil(plan) || isEmpty(plan)) {
    _cache.removeItem(_cache.Key.quotesCache);
    throw new Error('Term not found for the selected product.');
  }

  // Updates plan cache.
  updatePaymentPlanCache(plan);

  // Updates product cache.
  updateProductCache(plan, quotes);

  // Updates cached payment plan.
  await _payPlan.setPaymentPlan();

  // Throw named error to warn user about rate changes on the selected product.
  const termsMatch = await termMatchesPlan(plan, rate.term);
  if (!termsMatch) {
    const error = new Error("Quote found doesn't match selected term.");
    error.name = 'warnPrices';
    throw error;
  }
};

/**
 * Parses plans to plan index.
 * @param coverage Quote coverage.
 * @param plans Quote plans.
 * @returns Parsed plan index.
 */
const parseToPlanIndex = (
  coverage: QuotesModels.Coverage,
  plans: QuotesModels.Plan[]
): QuotesServiceModels.PlanIndex => {
  const planIndex = plans?.reduce(
    (plansResult: QuotesServiceModels.PlanIndex, plan: QuotesModels.Plan) => {
      const index = plan['@Name'];
      const planData = {
        ...plan,
        covId: coverage['@CovId'],
        covCode: coverage['@Code'],
        covDescription: coverage['@Description']
      } as QuotesServiceModels.IPlanData;
      plansResult[index] = planData;
      return plansResult;
    },
    {}
  );

  return planIndex;
};

/**
 * Parses coverages to index.
 * @param coverages Coverages to be parsed.
 * @returns Parsed coverages.
 */
const getCoveragesIndex = (
  coverages: QuotesModels.Coverage[]
): QuotesServiceModels.CoverageIndex => {
  const index = coverages?.reduce(
    (result: QuotesServiceModels.CoverageIndex, coverage) => {
      const plans = coverage?.Plans;
      const index = coverage?.['@Code'];

      const planIndex = parseToPlanIndex(coverage, plans);

      result[index] = planIndex;
      return result;
    },
    {}
  );

  return index;
};

/**
 * Parses coverages to coverage details.
 * @param coverages Coverages to be parsed.
 * @returns Parsed coverage details
 */
const getCoverageDetails = (
  coverages: QuotesModels.Coverage[]
): QuotesServiceModels.CoverageDetail[] => {
  return coverages.map((cov: QuotesModels.Coverage) => ({
    code: cov['@Code'],
    covId: cov['@CovId'],
    description: cov['@Description'],
    surcharges: cov.Surcharges
  }));
};

/**
 * Gets only coverages lower than contracted coverage.
 * @param details Coverage details to filter.
 * @returns Filtered coverage details.
 */
const filterCoverageDetails = (
  details: QuotesServiceModels.CoverageDetail[]
): QuotesServiceModels.CoverageDetail[] => {
  let result = details;
  const contract = getCurrentContract();
  if (contract) {
    const res = resources.levelsOrder as { [key: string]: number };
    result = details.filter(
      (detail: QuotesServiceModels.CoverageDetail) =>
        res[detail.code] >= (res[contract?.plan?.covCode ?? ''] || 0)
    );
  }

  return result;
};

/**
 * Sorts coverage details.
 * @param details Coverage details to be sorted.
 * @returns Sorted coverage details.
 */
const sortCoverageDetails = (
  details: QuotesServiceModels.CoverageDetail[]
): QuotesServiceModels.CoverageDetail[] => {
  let result = details;
  const res = resources.levelsOrder as { [key: string]: number };
  if (res) {
    result = details.sort(
      (
        a: QuotesServiceModels.CoverageDetail,
        b: QuotesServiceModels.CoverageDetail
      ) => {
        const aux1 = res[a.code];
        const aux2 = res[b.code];

        if (aux1 < aux2) return -1;
        if (aux1 > aux2) return 1;
        if (aux1 === aux2 || !aux1 || aux2) return 0;
        return 0;
      }
    );
  }

  return result;
};

/**
 * Gets the coverages for the ppm/vsc program.
 * @returns Coverages for the ppm/vsc program.
 */
export const getCoverages = async (
  programId?: string
): Promise<{
  surcharges: QuotesModels.Surcharge[];
  coveragesIndex: QuotesServiceModels.CoverageIndex;
  preferred?: QuotesModels.PreferredCoverage;
  coveragesDetails?: QuotesServiceModels.CoverageDetail[];
  programId?: string;
  basePrograms?: string[];
  upgradePrograms?: string[];
}> => {
  const useEstMiles = await _vehicleServices.useEstimatedMiles(); // 0g09162021 estimated/actual miles label
  const quotes = await getSingleQuote(useEstMiles, programId);
  if (!quotes || isNil(quotes)) {
    return { surcharges: [], coveragesIndex: {} };
  }
  const program = quotes;
  const coverages = program.Coverages;
  // Gets coverage index.
  const index = getCoveragesIndex(coverages);
  // Gets detailed coverage data like code, id and description.
  let details = getCoverageDetails(coverages);
  // Gets only coverages lower than contracted coverage.
  details = filterCoverageDetails(details);
  // Sort coverages from high to low.
  details = sortCoverageDetails(details);

  // we need to display only surchagers that are installed (IsUsed = 1)
  const installedSurcharges = program?.Surcharges?.filter(
    (s) => s.IsUsed === '1'
  );

  return {
    surcharges: installedSurcharges,
    coveragesIndex: index,
    preferred: program.Preferred,
    coveragesDetails: details,
    programId: program.Value,
    basePrograms: program.BasePrograms,
    upgradePrograms: program.UpgradePrograms
  };
};
